import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Trash2, Flame, Loader2, AlertTriangle } from 'lucide-react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';

// Capture la position GPS la plus précise possible en watchPosition,
// en gardant la meilleure mesure pendant un délai donné, et résout en
// dessous d'un seuil de précision « confortable ».
//
// Pourquoi : un seul getCurrentPosition donne souvent une lecture cellulaire
// (50-500 m). watchPosition force le GPS à se mettre à jour plusieurs fois,
// la précision tombe rapidement à 5-15 m.
function captureBestPosition({
  goodAccuracy = 12, // résoudre dès qu'on a ≤ 12 m
  hardTimeout = 12000, // mais au bout de 12 s, prendre ce qu'on a
} = {}) {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('GPS indisponible sur cet appareil'));
      return;
    }
    let best = null;
    let resolved = false;
    let watchId = null;
    let timer = null;

    const finish = (val) => {
      if (resolved) return;
      resolved = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (timer) clearTimeout(timer);
      if (val) resolve(val);
      else reject(new Error("Position GPS introuvable"));
    };

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const acc = pos.coords.accuracy;
        if (!best || acc < best.acc) {
          best = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            acc,
          };
        }
        // Si on est déjà assez précis, on arrête tout de suite.
        if (acc <= goodAccuracy) finish(best);
      },
      (err) => {
        // Si on a déjà une lecture, on la garde, sinon on remonte l'erreur.
        if (best) finish(best);
        else finish(null);
        if (err && err.code === 1) reject(new Error('Permission GPS refusée'));
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: hardTimeout }
    );

    timer = setTimeout(() => finish(best), hardTimeout);
  });
}

const TYPE_META = {
  poubelle: {
    label: 'Poubelle',
    color: '#eab308', // jaune (yellow-500)
    successMsg: 'Poubelle cartographiée',
    Icon: Trash2,
  },
  cendrier: {
    label: 'Cendrier',
    color: '#f97316', // orange
    successMsg: 'Cendrier cartographié',
    Icon: Flame,
  },
};

export default function Home() {
  const nav = useNavigate();

  // État du « busy » par type (pour désactiver et afficher un spinner sur le bouton cliqué)
  const [busy, setBusy] = useState(null); // 'poubelle' | 'cendrier' | null
  const [toast, setToast] = useState(null); // { msg, kind: 'success' | 'error', acc?: number }
  const toastTimer = useRef(null);

  // File inputs déclenchés directement depuis Home pour ne PAS perdre le user gesture
  // (iOS Safari bloque le programmatic click après une navigation React Router).
  // On ouvre la caméra ICI, et on navigue vers /capture (ou /zone-sensible) avec la photo en state.
  const avaloirFileRef = useRef(null);
  const zsFileRef = useRef(null);

  const onAvaloirPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    nav('/capture', { state: { photo: f } });
    // Reset pour autoriser une nouvelle sélection plus tard
    e.target.value = '';
  };

  const onZsPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    nav('/zone-sensible', { state: { photo: f } });
    e.target.value = '';
  };

  // GPS pré-chauffé : on lance watchPosition dès l'arrivée sur la Home
  // pour avoir une lecture fraîche en cache quand l'utilisateur appuie sur
  // Poubelle/Cendrier. C'est ce qui rend le clic « quasi instantané ».
  // On stocke la meilleure mesure récente (acc + timestamp) dans un ref pour
  // éviter les re-renders inutiles.
  const lastFix = useRef(null); // { lat, lng, acc, ts }

  useEffect(() => {
    if (!('geolocation' in navigator)) return;
    let watchId = null;
    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          lastFix.current = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            acc: pos.coords.accuracy,
            ts: Date.now(),
          };
        },
        () => {
          // ignore : si watchPosition échoue (permission, etc.), le fallback
          // dans onQuickPoint affichera l'erreur au moment du clic
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
      );
    } catch {
      // ignore : on retombera sur captureBestPosition au clic
    }
    return () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const showToast = (msg, kind = 'success', acc = null) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, kind, acc });
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  };

  // Position utilisable « tout de suite » pour un quick-point :
  // - on accepte le cache si fraîche (<30s) et raisonnable (acc ≤50m)
  // - sinon on tente un capture courte (≤30m / 3s)
  // - sinon on prend ce qu'on a au bout des 3s (au pire un fix cellulaire)
  const getQuickFix = async () => {
    const cached = lastFix.current;
    const fresh = cached && Date.now() - cached.ts < 30000;
    if (fresh && cached.acc <= 50) {
      return { lat: cached.lat, lng: cached.lng, acc: cached.acc };
    }
    try {
      return await captureBestPosition({ goodAccuracy: 30, hardTimeout: 3000 });
    } catch (err) {
      // Si la capture courte a échoué mais qu'on a un cache, on l'utilise
      // quel que soit son âge — mieux que rien.
      if (cached) return { lat: cached.lat, lng: cached.lng, acc: cached.acc };
      throw err;
    }
  };

  const onQuickPoint = async (type) => {
    if (busy) return;
    const meta = TYPE_META[type];
    setBusy(type);
    try {
      const pos = await getQuickFix();

      // Reverse geocoding ville (best-effort)
      let city = null;
      let cityCode = null;
      try {
        const r = await fetch(
          `https://api-adresse.data.gouv.fr/reverse/?lon=${pos.lng}&lat=${pos.lat}`
        );
        const j = await r.json();
        const feat = j.features?.[0];
        if (feat) {
          city = feat.properties.city || feat.properties.municipality || null;
          cityCode = feat.properties.citycode || null;
        }
      } catch {
        // ignore
      }

      await api.post('/api/quick-points', {
        type,
        latitude: pos.lat,
        longitude: pos.lng,
        accuracy_m: pos.acc,
        city,
        city_code: cityCode,
      });

      showToast(meta.successMsg, 'success', pos.acc);
    } catch (err) {
      showToast(
        (err && err.message) || `Erreur lors de l'enregistrement du ${meta.label.toLowerCase()}`,
        'error'
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="home">
      <Header />

      {/* File inputs cachés — clicks directs depuis les boutons (préservation du user gesture) */}
      <input
        ref={avaloirFileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onAvaloirPhoto}
        style={{ display: 'none' }}
      />
      <input
        ref={zsFileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onZsPhoto}
        style={{ display: 'none' }}
      />

      <main className="home__main">
        <button
          type="button"
          className="home__cta"
          onClick={() => avaloirFileRef.current?.click()}
          aria-label="Cartographier un avaloir"
        >
          <Camera size={48} strokeWidth={2.2} aria-hidden="true" />
          <span className="home__cta-text">CARTOGRAPHIER<br/>UN AVALOIR</span>
        </button>

        {/* Quick points : Poubelle (jaune) + Cendrier (orange) */}
        <div className="home__quick">
          {(['poubelle', 'cendrier']).map((type) => {
            const m = TYPE_META[type];
            const Icon = m.Icon;
            const isBusy = busy === type;
            return (
              <button
                key={type}
                type="button"
                className={`home__quick-btn home__quick-btn--${type}`}
                onClick={() => onQuickPoint(type)}
                disabled={!!busy}
                aria-label={`Cartographier ${m.label.toLowerCase()}`}
                style={{ background: m.color }}
              >
                {isBusy ? (
                  <Loader2 size={44} className="home__spin" aria-hidden="true" />
                ) : (
                  <Icon size={44} strokeWidth={2.2} aria-hidden="true" />
                )}
                <span className="home__quick-text">{m.label}</span>
              </button>
            );
          })}
        </div>

        {/* Zone Sensible : photo + commentaire */}
        <button
          type="button"
          className="home__zone-btn"
          onClick={() => zsFileRef.current?.click()}
          aria-label="Cartographier une zone sensible"
        >
          <AlertTriangle size={44} strokeWidth={2.2} aria-hidden="true" />
          <span className="home__zone-text">ZONE SENSIBLE</span>
        </button>

        <div className="home__secondary">
          <button
            type="button"
            className="vrt-btn vrt-btn--ghost home__sec-btn"
            onClick={() => nav('/map')}
          >
            Voir la carte
          </button>
          <button
            type="button"
            className="vrt-btn vrt-btn--ghost home__sec-btn"
            onClick={() => nav('/list')}
          >
            Liste
          </button>
        </div>
      </main>

      {toast && (
        <div
          className={`home__toast home__toast--${toast.kind}`}
          role="status"
          aria-live="polite"
        >
          <span className="home__toast-msg">{toast.msg}</span>
          {toast.kind === 'success' && toast.acc != null && (
            <span className="home__toast-acc">±{Math.round(toast.acc)} m</span>
          )}
        </div>
      )}

      <Footer />

      <style>{`
        .home {
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          background: var(--vrt-bg);
          position: relative;
        }
        .home__main {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: stretch;
          gap: 16px;
          padding: 16px 16px 16px;
          max-width: 720px;
          width: 100%;
          margin: 0 auto;
        }
        .home__cta {
          flex: 1.4;
          width: 100%;
          min-height: 0;
          border-radius: 26px;
          border: none;
          background: var(--vrt-navy);
          color: #fff;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 22px;
          padding: 24px;
          box-shadow: 0 10px 28px rgba(26, 40, 73, 0.28);
          transition: transform .08s ease, background .15s ease, box-shadow .15s ease;
          cursor: pointer;
        }
        .home__cta :global(svg),
        .home__cta svg {
          width: clamp(56px, 14vw, 96px);
          height: clamp(56px, 14vw, 96px);
        }
        .home__cta:hover {
          background: var(--vrt-navy-700);
          box-shadow: 0 12px 32px rgba(26, 40, 73, 0.38);
        }
        .home__cta:active { transform: scale(0.98); }
        .home__cta:focus-visible {
          outline: 3px solid var(--vrt-teal);
          outline-offset: 4px;
        }
        .home__cta-text {
          font-size: clamp(22px, 6vw, 38px);
          font-weight: 800;
          letter-spacing: 0.06em;
          text-align: center;
          line-height: 1.15;
        }

        .home__quick {
          flex: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
          width: 100%;
          min-height: 0;
        }
        .home__quick-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 18px;
          min-height: 0;
          height: 100%;
          padding: 22px 16px;
          border: none;
          border-radius: 22px;
          color: #fff;
          font-size: clamp(20px, 5vw, 32px);
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          cursor: pointer;
          box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
          transition: transform .08s ease, filter .15s ease, box-shadow .15s ease;
        }
        .home__quick-btn svg {
          width: clamp(48px, 12vw, 84px);
          height: clamp(48px, 12vw, 84px);
        }
        .home__quick-btn:hover:not(:disabled) {
          filter: brightness(1.08);
          box-shadow: 0 8px 22px rgba(0, 0, 0, 0.24);
        }
        .home__quick-btn:active:not(:disabled) { transform: scale(0.97); }
        .home__quick-btn:disabled {
          opacity: 0.78;
          cursor: progress;
        }
        .home__quick-text { white-space: nowrap; }
        .home__spin { animation: home-spin 1s linear infinite; }
        @keyframes home-spin {
          to { transform: rotate(360deg); }
        }

        .home__zone-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
          width: 100%;
          padding: 18px 16px;
          border: none;
          border-radius: 22px;
          background: #ef4444; /* rouge clair */
          color: #fff;
          font-size: clamp(20px, 5vw, 30px);
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          cursor: pointer;
          box-shadow: 0 8px 22px rgba(239, 68, 68, 0.32);
          transition: transform .08s ease, filter .15s ease;
          min-height: 80px;
          flex: 0 0 auto;
        }
        .home__zone-btn svg {
          width: clamp(36px, 9vw, 56px);
          height: clamp(36px, 9vw, 56px);
        }
        .home__zone-btn:hover { filter: brightness(1.08); }
        .home__zone-btn:active { transform: scale(0.98); }
        .home__zone-text { white-space: nowrap; }

        .home__secondary {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          justify-content: center;
          width: 100%;
          flex: 0 0 auto;
        }
        .home__sec-btn {
          flex: 1 1 140px;
          min-height: 48px;
        }

        /* Toast */
        .home__toast {
          position: fixed;
          left: 50%;
          bottom: calc(env(safe-area-inset-bottom, 0px) + 28px);
          transform: translateX(-50%);
          display: inline-flex;
          align-items: center;
          gap: 10px;
          max-width: calc(100vw - 32px);
          padding: 14px 20px;
          border-radius: 999px;
          font-size: 15px;
          font-weight: 600;
          color: #fff;
          box-shadow: 0 14px 40px rgba(0, 0, 0, 0.32);
          z-index: 1000;
          animation: home-toast-in 0.18s ease-out;
        }
        .home__toast--success { background: var(--vrt-teal); }
        .home__toast--error   { background: var(--vrt-danger); }
        .home__toast-acc {
          opacity: 0.9;
          font-weight: 500;
          font-size: 13px;
          padding: 2px 8px;
          border-radius: 999px;
          background: rgba(0,0,0,0.16);
        }
        @keyframes home-toast-in {
          from { opacity: 0; transform: translate(-50%, 8px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }

        @media (max-width: 480px) {
          .home__main { padding: 12px 12px 12px; gap: 12px; }
          .home__cta { padding: 18px 14px; gap: 16px; border-radius: 22px; }
          .home__quick { gap: 10px; }
          .home__quick-btn { padding: 16px 10px; gap: 12px; border-radius: 18px; }
        }
      `}</style>
    </div>
  );
}
